// All axios calls for client document collection live here so the components
// stay free of transport concerns. Every response is wrapped in a
// GenericResponse envelope, so the payload sits at response.data.data.

import axios from "axios";
import { API_URL } from "const/constants";

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const extractErrorMessage = (err) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || DEFAULT_ERROR_MESSAGE;

export const withErrorMessage = (promise) =>
  promise.catch((err) => {
    throw new Error(extractErrorMessage(err));
  });

const documentsBase = (clientId) => `${API_URL}/clients/${clientId}/documents`;

/** Shared React Query key so the tab's list and the create dialog invalidate the same cache entry. */
export const CLIENT_DOCUMENT_REQUESTS_QUERY_KEY = "client-document-requests";

/** Newest-first list of document requests for a client. */
export const fetchDocumentRequests = (clientId) =>
  withErrorMessage(
    axios.get(`${documentsBase(clientId)}/requests`).then((r) => r.data?.data ?? [])
  );

/**
 * Create a new document request. body: { title, items: [{ label, instructions,
 * required, maxFiles }] }. maxFiles must be null or an integer >= 1, the
 * backend rejects a non-positive value naming the offending item.
 */
export const createDocumentRequest = (clientId, body) =>
  withErrorMessage(
    axios.post(`${documentsBase(clientId)}/requests`, body).then((r) => r.data?.data)
  );

/** Revoke an active document request. */
export const revokeDocumentRequest = (clientId, requestId) =>
  withErrorMessage(
    axios.post(`${documentsBase(clientId)}/requests/${requestId}/revoke`).then((r) => r.data?.data)
  );

/**
 * Resend the ask for a request. target: "CLIENT" forces a client email even for
 * partner-routed rounds, "PARTNER" notifies the referral partner instead, null/undefined
 * uses the default routing (partner-notify when partner-assigned, client email otherwise).
 * Reupload rounds email the reupload message, not the original new-request message.
 */
export const resendDocumentRequest = (clientId, requestId, target) =>
  withErrorMessage(
    axios
      .post(`${documentsBase(clientId)}/requests/${requestId}/resend`, target ? { target } : {})
      .then((r) => r.data?.message)
  );

/**
 * Read-only mirror of the documents held by the business modules, grouped by module then entry.
 * The backend drops any module the caller cannot read, so an empty result is a real answer.
 */
export const fetchModuleDocuments = (clientId) =>
  withErrorMessage(
    axios.get(`${documentsBase(clientId)}/module-documents`).then((r) => r.data?.data ?? [])
  );

export const CLIENT_MODULE_DOCUMENTS_QUERY_KEY = "client-module-documents";

export const CLIENT_MODULE_ENTRIES_QUERY_KEY = "client-module-entries";

/** Every module entry this client has, permission filtered, for the show-in-module picker. */
export const fetchModuleEntries = (clientId) =>
  withErrorMessage(
    axios.get(`${documentsBase(clientId)}/module-entries`).then((r) => r.data?.data ?? [])
  );

/** Show a collected document inside a module entry. The file is not copied or moved. */
export const linkDocumentToModule = (clientId, documentId, moduleType, moduleEntryId) =>
  withErrorMessage(
    axios
      .post(`${documentsBase(clientId)}/${documentId}/links`, { moduleType, moduleEntryId })
      .then((r) => r.data?.data)
  );

/** Removes the link only. The file stays where it was collected. */
export const unlinkDocumentFromModule = (clientId, documentId, linkId) =>
  withErrorMessage(
    axios
      .delete(`${documentsBase(clientId)}/${documentId}/links/${linkId}`)
      .then((r) => r.data?.message)
  );

/** Documents linked into one module entry, for the Additional documents section. */
export const fetchLinkedDocuments = (moduleType, entryId) =>
  withErrorMessage(
    axios
      .get(`${API_URL}/modules/${moduleType}/${entryId}/linked-documents`)
      .then((r) => r.data?.data ?? [])
  );

export const LINKED_DOCUMENTS_QUERY_KEY = "module-linked-documents";

/**
 * Ask for corrected copies. items: [{ itemId, reason }]. routing: { assignee: "CLIENT"|"PARTNER",
 * assigneePartnerId } mirrors the first-time request routing — PARTNER notifies the referral
 * partner (no client email; the partner collects or uploads like a new request), anything else
 * emails the client the reupload message. Null routing inherits the parent's routing.
 */
export const requestReupload = (clientId, requestId, items, routing) =>
  withErrorMessage(
    axios
      .post(`${documentsBase(clientId)}/requests/${requestId}/reupload`, {
        items,
        ...(routing?.assignee ? { assigneeType: routing.assignee } : {}),
        ...(routing?.assignee === "PARTNER" && routing?.assigneePartnerId
          ? { assigneePartnerId: routing.assigneePartnerId }
          : {}),
      })
      .then((r) => r.data?.data)
  );

/**
 * Staff filing a document they already have directly onto an item, bypassing the client's OTP
 * portal entirely. Lands as Collected immediately - there is no draft/submit step for staff.
 */
export const uploadDocumentAsStaff = (clientId, requestId, itemId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return withErrorMessage(
    axios
      .post(`${documentsBase(clientId)}/requests/${requestId}/items/${itemId}/upload`, formData)
      .then((r) => r.data?.data)
  );
};

/**
 * Nested request + document library view for a client, used by the detail
 * panel (not this tab). Included here so that panel can reuse this module
 * instead of duplicating the axios call.
 */
export const fetchDocumentLibrary = (clientId) =>
  withErrorMessage(axios.get(`${documentsBase(clientId)}/library`).then((r) => r.data?.data ?? []));

/** Shared React Query key for one request's timeline. */
export const CLIENT_DOCUMENT_EVENTS_QUERY_KEY = "client-document-request-events";

/** Oldest-first activity timeline for a single request. */
export const fetchRequestEvents = (clientId, requestId) =>
  withErrorMessage(
    axios
      .get(`${documentsBase(clientId)}/requests/${requestId}/events`)
      .then((r) => r.data?.data ?? [])
  );

/**
 * Streams a collected document. The response is raw bytes rather than the
 * GenericResponse envelope, so this returns the blob and the filename the
 * server chose rather than reading response.data.data.
 */
export const downloadDocument = (clientId, documentId, fallbackFilename) =>
  withErrorMessage(
    axios
      .get(`${documentsBase(clientId)}/${documentId}/content`, { responseType: "blob" })
      .then((r) => ({
        blob: r.data,
        filename: filenameFromDisposition(r.headers?.["content-disposition"]) || fallbackFilename,
      }))
  );

const filenameFromDisposition = (disposition) => {
  if (!disposition) return "";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return "";
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : "";
};

/**
 * Soft-deletes a collected document. The backend marks it REMOVED and moves it to the
 * item's History rather than destroying the stored file.
 */
export const deleteDocument = (clientId, documentId) =>
  withErrorMessage(
    axios.delete(`${documentsBase(clientId)}/${documentId}`).then((r) => r.data?.message)
  );
